import { useState } from "react";
import { Moon } from "lucide-react";

import { useAuth } from "../../Services/AuthContext";
import { useGoingOut } from "../../Services/GoingOutContext";
import GoingOutPicker from "./GoingOutPicker";
import { friendSummary } from "./goingOutLabels";
import type { GoingOutStatus } from "../../data/types";
import "../../Styles/GoingOut.css";

/**
 * Kvällsfrågan på kartan: en fråga, och sedan ingenting.
 *
 * FRÅGAN FÖRSVINNER NÄR DEN ÄR BESVARAD. Har man svarat OUT, MAYBE eller
 * NOT_TONIGHT ritar den här komponenten ingenting alls — kartan är en
 * karta, och en fråga man redan besvarat är bara något som ligger i vägen
 * för den. Svaret går fortfarande att ändra hela natten, men i menyn
 * bakom avataren och på den egna profilen. Se GoingOutStatusControl.
 *
 * INGEN RUTA SOM POPPAR UPP. Det fanns en modal här som ställde frågan av
 * sig själv på kvällen, och den är borttagen med flit.
 *
 * Chippet finns dygnet runt. Vilken natt ett svar gäller avgörs ändå av
 * servern — svarar man på eftermiddagen gäller det kvällen som kommer, och
 * svarar man klockan två gäller det natten man redan är i.
 *
 * Servern skickar fortfarande shouldPrompt och promptWindowOpen. Ingenting
 * här läser dem, men de är kvar för att en framtida nudge ska kunna byggas
 * utan att räkna om nätter och kvällsfönster på nytt. Se api/goingOut.js.
 */

export default function GoingOutCTA() {
  const { isAuthenticated } = useAuth();
  const { state, isBusy, answer } = useGoingOut();

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  if (!isAuthenticated || !state) return null;

  // Besvarad kväll: bort från kartan. Inte dold, inte hopfälld — borta.
  if (state.me) return null;

  const choose = async (status: GoingOutStatus) => {
    await answer(status);
    setIsPickerOpen(false);
  };

  // OUT och MAYBE räknade var för sig. Raden får aldrig påstå att den som
  // svarat "kanske" är ute.
  const summary = friendSummary(state.counts);

  return (
    <div className="going-out-cta">
      {isPickerOpen ? (
        <GoingOutPicker
          current={null}
          disabled={isBusy}
          onAnswer={choose}
          onCancel={() => setIsPickerOpen(false)}
        />
      ) : (
        <>
          <button
            type="button"
            className="going-out-chip"
            onClick={() => setIsPickerOpen(true)}
            disabled={isBusy}
          >
            <Moon size={14} strokeWidth={2.2} />
            Going out tonight?
          </button>

          {summary && <p className="going-out-friends">{summary}</p>}
        </>
      )}
    </div>
  );
}
